import { Response } from 'node-fetch';
import { buildUrl } from "build-url-ts"

import { remoteApisUrls } from './constants';
import fetch from './fetch';
import { leaveQueryBreadCrumb } from './helpers';
import { ObjectType, StringNumber } from '@vromo/models/helpers/models';
import { IntegrationLocation, IntegrationProfile, Platform } from '@vromo/models/integrations';
import { GraphError } from './graph';
import { getRunEnv } from '@vromo/utils';



export type IntegrationApiLocationAction = "list" | "fetch" | "enable" | "disable" | "create"
export type fetchParams<Q, B> = { query?: Q, body?: B }

export type TrayEndpoint<R = ObjectType, B = ObjectType & {
    vromo_group_id?: string;
    vromo_root_group_id?: string;
    login_token?: string;
}> = (bodyData?: B) => Promise<R>

export type VercelEndpoint<R = ObjectType, Q = ObjectType, B = ObjectType> = ({ query, body }?: fetchParams<Q, B>) => Promise<R>




/** vromo Tray integrations api methods */
export class IntegrationsApi {

    /** top level parent group */
    private _rootGroup: string | undefined;
    /** current vromo group */
    private _group: string | undefined;
    /** current integration service session */
    private _session: string | undefined;

    private _env = getRunEnv()

    constructor(
        { session, group, rootGroup }:
            { session?: string, group?: StringNumber, rootGroup?: StringNumber } = {}
    ) {
        if (session)
            this._session = session
        if (group)
            this._group = group.toString()
        if (rootGroup)
            this._rootGroup = rootGroup.toString()
    }

    set group(value: StringNumber) {
        this._group = value.toString()
    }
    set rootGroup(value: StringNumber) {
        this._rootGroup = value.toString()
    }
    set session(value: string | undefined) {
        this._session = value
    }

    get env(): "prod" | "dev" {
        return this._env === 'production' ? "prod" : "dev"
    }

    get apiUrls() {
        return remoteApisUrls[this.env]
    }

    /** @deprecated */
    private get _defaultBody() {
        return {
            ...this._session && { session: this._session },
            ...this._group && { vromo_group_id: this._group },
        }
    }

    private get _defaultHeader() {
        return {
            ...this._session && { vromo_session: this._session },
            ...this._group && { vromo_group: this._group },
            ...this._rootGroup && { vromo_account: this._rootGroup },
        }
    }



    /** R= reponse type, B= post body type 
    * @deprecated
    */
    private _endpoint<R = ObjectType, B = ObjectType>(path: string, protocol: 'get' | 'post', msg?: string): TrayEndpoint<R, B> {

        const query = async (bodyData?: B) => {

            const body = JSON.stringify(
                {
                    ...this._defaultBody,
                    ...bodyData
                })

            leaveQueryBreadCrumb({ data: body, path, type: 'request', protocol, msg: `${protocol.toUpperCase()}: ${msg}` })
            const response: Response = await fetch(path, {
                method: protocol.toUpperCase(),
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    ...protocol === "get" && this._defaultHeader
                },
                ...protocol !== "get" && {
                    body
                }
            });
            const responseBody = await response.clone().json()
                .catch((err) => response.clone().text())


            if (!response.ok) {
                const faultyResponse = responseBody as any
                return Promise.reject(new GraphError(
                    response.status,
                    [
                        // tray sometimes returns error information in .msg or .message
                        ...faultyResponse.msg ? [faultyResponse.msg] : [],
                        ...faultyResponse.message ? [faultyResponse.message] : [],
                        ...response.statusText ? [response.statusText] : [],
                        ...typeof responseBody === 'string' ? [responseBody] : [],
                    ].join(". ")
                ))

            }
            return responseBody as R
        }

        return query
    }

    /** R= reponse type, B= post body type */
    private _endpointV2<R = ObjectType, Q = ObjectType, B = ObjectType>(
        path: string,
        protocol: 'get' | 'post' | "patch",
        msg?: string): VercelEndpoint<R, Q, B> {

        const query = async ({ query, body }: fetchParams<Q, B> = {}): Promise<R> => {
            // todo: better url construction
            const urlPath = new URL(path)
            const urlParams = new URLSearchParams(urlPath.search);
            const newUrl = buildUrl(path.replace(urlPath.search, ""), {
                queryParams: {
                    ...urlParams.get("tray") && { tray: urlParams.get("tray") },
                    ...query
                }
            })

            leaveQueryBreadCrumb({ data: body, path: newUrl, type: 'request', protocol, msg: `${protocol.toUpperCase()}: ${msg}` })
            const response: Response = await fetch(newUrl, {
                method: protocol.toUpperCase(),
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    ...this._defaultHeader,
                },
                ...body && {
                    body: JSON.stringify(body)
                },
                query: { ...query }
            });
            const responseBody = await response.clone().json().catch(() => response.text())

            if (!response.ok || typeof responseBody === "string") {
                const faultyResponse = responseBody as any
                return Promise.reject(new GraphError(
                    response.status,
                    [
                        // tray sometimes returns error information in .msg or .message
                        ...faultyResponse.msg ? [faultyResponse.msg] : [],
                        ...faultyResponse.message ? [faultyResponse.message] : [],
                        ...response.statusText ? [response.statusText] : [],
                        ...typeof responseBody === 'string' ? [responseBody] : [],
                    ].join(". ")
                ))
            }
            return responseBody as R
        }

        return query
    }

    /** list all integrations */
    get list(): VercelEndpoint<{
        message: string,
        result: IntegrationProfile[]
    }> {
        return this._endpointV2(
            this.apiUrls.integrations.profiles,
            "get",
            "list integrations"
        )
    }


    /** get integration session toke */
    get sessionToken(): TrayEndpoint<
        {
            session: string;
            message?: string;
        },
        {
            vromo_group_id: string;
            login_token: string;
        }
    > {
        return this._endpoint(this.apiUrls.integrations.session, "post", "POST: get new tray session token")
    }

    /** creates a new integration profile */
    get createProfile(): TrayEndpoint<
        {
            wizard_url: string;
            created: boolean;
            message: string;
            auth_only_url: string;
            enabled_url: string;
            instance_id: string
        },
        {
            /** @note rather than vromo user email, provide the group name */
            vromo_email: string;
            vromo_access_token?: string;
            platform: Platform;
        }
    > {
        return this._endpoint(this.apiUrls.integrations.tray.create, "post", "POST: create integrations")
    }

    /** deletes an a new integration instance ( tray ) */
    deleteInstance(url = ""): TrayEndpoint<
        {
            message?: string;
            deleted?: boolean;
        },
        {
            instance_id?: string;
        }
    > { return this._endpoint(url, "post", "POST: delete integration") }

    /** create a integration location
     * todo: create multiple integrations
     */
    createLocations(platform: Platform): VercelEndpoint<
        {
            result: IntegrationLocation,
            messasge?: string
        },
        {
            /** will differ based on the type of integration */
            data: any
            /** not all the locations require a vromo access token, some of these already have a token configured at profile level */
            vromo_access_token?: string
        }
    > {
        return this._endpointV2(this.apiUrls.integrations.locations(platform), "post", "create new integration locations(s)")
    }

    /** query stored integration locations. perform an action on integration  mgmt locations */
    queryLocations(url = ""): VercelEndpoint<
        { result?: IntegrationLocation[] },
        { // todo: type as "enable" | "disable" 
            action: IntegrationApiLocationAction;
        }
    > {
        return this._endpointV2(url, "get", "fetch stored / sync integration locations")
    }

    /** query stored integration locations */
    toogleLocation(url = ""): VercelEndpoint<
        {
            result?: IntegrationLocation[];
        },
        never,
        {
            // todo: type as "enable" | "disable" 
            action: IntegrationApiLocationAction;
            /** location_id that needs to be enabled / disabled */
            id: string
        }
    > {
        return this._endpointV2(url, "patch", "query action on integration location")
    }


    /** finish the config of a new integration */
    finishConfig(url: string): TrayEndpoint<
        {
            message?: string;
            completed?: boolean;
        }
    > { return this._endpoint(url, "post", "POST: finish integration configuration") }


    /** get auth url for certain new integration ie kitchen uber*/
    auth(platform: Platform): VercelEndpoint<{ result: { authUrl: string } }> {
        return this._endpointV2(this.apiUrls.integrations.auth(platform), "get", "POST: start integration configuration")
    }



}


